import { useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { formatNumberWithTwoDecimals } from "../../../../../utils/FormatUtils";
import { toWorkDayFromInput, WEEK_DAYS } from "../../../../../utils/WorkDayUtils";

/**
 * Avisa o operador quando o dia escolhido não é coberto pelo plano do assinante.
 *
 * O atendimento acontece normalmente — o dia nunca barra ninguém —, mas o preço sai diferente
 * do que o cliente costuma pagar. Sem este aviso a recepção descobre isso só quando o valor
 * aparece na tela, no meio do atendimento, e sem nada que explique a diferença.
 *
 * Serve tanto para a agenda quanto para a venda: as duas concedem benefício pela mesma regra
 * (PlanDiscountResolver, no backend).
 */
const PlanOffDayNotice = ({ customerId, date, serviceId }) => {

    const [subscription, setSubscription] = useState(null);

    useEffect(() => {
        if (!customerId) {
            setSubscription(null);
            return;
        }

        let active = true;
        axiosInstance.get(`${Api.subscription.subscription}/customer/id/${customerId}`)
            .then(resp => {
                if (active) setSubscription(resp.data || null);
            })
            // 404 é o cliente sem contrato de clube, que é a maioria: não é erro.
            .catch(() => {
                if (active) setSubscription(null);
            });

        return () => { active = false; };
    }, [customerId]);

    const plan = subscription?.plan;
    const benefitDays = useMemo(() => plan?.benefitDays || [], [plan]);

    // Serviço que não está no plano é cobrado integralmente em qualquer dia: avisar sobre o
    // dia ali só confundiria, porque o preço não muda por causa dele.
    const planProduct = useMemo(() => {
        if (!plan?.planProducts || !serviceId) return null;
        return plan.planProducts.find(pp => String(pp.productId) === String(serviceId)) || null;
    }, [plan, serviceId]);

    if (!plan || !date || benefitDays.length === 0) {
        return null;
    }

    // Sem serviço escolhido ainda, o aviso vale pelo plano: o operador está montando o
    // agendamento e é melhor saber do dia antes de escolher o serviço.
    if (serviceId && (!planProduct || !(planProduct.discountPercentage > 0))) {
        return null;
    }

    if (benefitDays.includes(toWorkDayFromInput(date))) {
        return null;
    }

    const offDayDiscount = Number(plan.offDayDiscountPercentage || 0);
    const benefitDayLabels = WEEK_DAYS
        .filter(day => benefitDays.includes(day.value))
        .map(day => day.label)
        .join(', ');

    return (
        <Alert variant="warning" className="mt-3 mb-3 py-3 px-3">
            <div className="d-flex align-items-start gap-2">
                <i className="fa fa-triangle-exclamation mt-1"></i>
                <div style={{ fontSize: '0.85rem' }}>
                    {/* O título precisa de respiro do corpo: colado, a frase toda vira um
                        bloco só e o operador não acha a informação de preço. */}
                    <strong className="d-block mb-1">
                        Dia sem benefício integral do plano {plan.name ? `(${plan.name})` : ''}.
                    </strong>
                    {offDayDiscount > 0
                        ? `Neste dia o desconto é de ${formatNumberWithTwoDecimals(offDayDiscount)}%, não a isenção do plano.`
                        : 'Neste dia o serviço é cobrado pelo valor cheio.'}
                    {' '}O benefício integral vale em {benefitDayLabels}. O atendimento pode ser lançado
                    normalmente e não consome a cota do cliente.
                </div>
            </div>
        </Alert>
    );
};

export default PlanOffDayNotice;
